import { useEffect } from "react";
import { useIntegrations, useSyncStatus, SyncStatus } from "@moonscape/shared";

interface UseAnySyncStatusOptions {
  /** Poll integrations list interval in ms (default: 10000) */
  integrationsPollInterval?: number;
  /** Poll sync status interval in ms (default: 3000) */
  syncStatusPollInterval?: number;
  /** Mock sync status for testing (overrides real status) */
  mockSyncStatus?: Partial<SyncStatus> | null;
}

interface UseAnySyncStatusResult {
  /** Whether any exchange is currently syncing */
  isAnySyncing: boolean;
  /** Detailed sync status from /sync/status/:id endpoint */
  syncStatus: SyncStatus | undefined;
  /** Whether the sync status is actively polling */
  isPolling: boolean;
  /** The exchange ID that's currently syncing */
  syncingExchangeId: string | undefined;
  /** The integration object that's syncing */
  activeSyncIntegration: any | undefined;
}

/**
 * Hook to detect if any exchange is syncing and get detailed status.
 * Combines useIntegrations (to find syncing exchange) + useSyncStatus (for details).
 *
 * Usage:
 *   const { isAnySyncing, syncStatus, isPolling } = useAnySyncStatus();
 *
 * Testing:
 *   const { syncStatus } = useAnySyncStatus({
 *     mockSyncStatus: { state: 'in_progress', overall_progress: 45, current_step: 'Fetching trades...' }
 *   });
 */
export const useAnySyncStatus = (
  options: UseAnySyncStatusOptions = {}
): UseAnySyncStatusResult => {
  const {
    integrationsPollInterval = 10000,
    syncStatusPollInterval = 3000,
    mockSyncStatus,
  } = options;

  // Get all integrations to find which one is syncing
  const { data: integrationsData, refetch: refetchIntegrations } = useIntegrations(
    "all",
    100,
    0
  );

  // Every non-terminal state in the import state machine
  // (positions/services/sync_state/transitions.py) — the widget was blind to
  // the queued/validating window at the start of a sync
  const ACTIVE_SYNC_STATES = [
    "queued",
    "starting",
    "validating_api_keys",
    "in_progress",
    "syncing",
  ];
  const activeSyncIntegration = integrationsData?.items?.find((i) =>
    ACTIVE_SYNC_STATES.includes(i.sync_state ?? "")
  );

  const syncingExchangeId = activeSyncIntegration?.exchange;

  // Get detailed sync status from /sync/status/:integration_id endpoint
  const { syncStatus: detailedSyncStatus, isPolling } = useSyncStatus({
    exchangeId: syncingExchangeId || "",
    enabled: !!syncingExchangeId && !mockSyncStatus,
    refetchInterval: syncStatusPollInterval,
  });

  const isAnySyncing = !!syncingExchangeId || !!mockSyncStatus;

  // Poll integrations list periodically to detect new syncs or completion
  useEffect(() => {
    if (mockSyncStatus) return; // Don't poll when mocking

    const interval = setInterval(() => {
      refetchIntegrations();
    }, integrationsPollInterval);

    return () => clearInterval(interval);
  }, [refetchIntegrations, integrationsPollInterval, mockSyncStatus]);

  // Use mock if provided, otherwise use real status
  const syncStatus = mockSyncStatus
    ? ({
        state: "in_progress",
        exchange: "mock",
        current_step: "Mock sync in progress...",
        overall_progress: 50,
        ...mockSyncStatus,
      } as SyncStatus)
    : detailedSyncStatus;

  return {
    isAnySyncing,
    syncStatus,
    isPolling: mockSyncStatus ? true : isPolling,
    syncingExchangeId: mockSyncStatus ? "mock" : syncingExchangeId,
    activeSyncIntegration,
  };
};
